/**
 * Arabic script letters, excluding digits and punctuation.
 *
 * Digits are left out deliberately: `٢٠٢٦` is written the same way in an
 * Arabic sentence and quoted inside an English one, so counting them would
 * make a date the deciding vote in a line that is otherwise English.
 */
const ARABIC_LETTERS = /[\u0620-\u064A\u066E\u066F\u0671-\u06D3\u06D5\u06FA-\u06FF\u0750-\u077F\u08A0-\u08BF\uFB50-\uFDFF\uFE70-\uFEFC]/g;
/** Latin letters, including the accented ranges. */
const LATIN_LETTERS = /[A-Za-z\u00C0-\u024F]/g;
function count(text, pattern) {
    return text.match(pattern)?.length ?? 0;
}
/**
 * The language a line should be spoken in.
 *
 * Decided by majority of letters rather than by presence, because agent lines
 * mix scripts in both directions: an Arabic sentence naming a project keeps a
 * few Latin letters, and an English one naming a person keeps a few Arabic
 * ones. Whichever script most of the sentence is written in is the one a
 * listener needs it read in; a handful of foreign letters inside it is a word
 * the voice will approximate, not a reason to read the whole line wrong.
 *
 * English is the default for anything with no letters at all — an emoji, a
 * number, a line of punctuation — which is both the app's own default and the
 * language whose voice is installed on essentially every device.
 */
export function speechLanguage(text) {
    return count(text, ARABIC_LETTERS) > count(text, LATIN_LETTERS) ? 'ar' : 'en';
}
/**
 * The BCP-47 tag to hand a speech engine for each language.
 *
 * Regionalised because both platforms want a full tag: a bare `ar` matches no
 * voice on iOS, and browsers match voices by prefix anyway, so naming a region
 * costs nothing and fails less. Modern Standard Arabic is what an agent
 * writes, and `ar-SA` is the tag every engine ships it under.
 */
export const SPEECH_LOCALES = {
    ar: 'ar-SA',
    en: 'en-US',
};
/**
 * Whether a voice list contains something able to read this language.
 *
 * Matching on the prefix, not the whole tag: a device with `ar-EG` installed
 * can read `ar-SA` text perfectly well, and an installed English voice is
 * `en-GB` as often as `en-US`. Tags arrive both hyphenated and underscored
 * depending on the platform, hence the normalisation.
 *
 * An empty list means "the platform has not told us", not "nothing is
 * installed" — browsers populate voices asynchronously and Android returns
 * nothing until its engine is bound — so callers treat empty as unknown and
 * try to speak regardless.
 */
export function hasVoiceFor(language, voiceTags) {
    const prefix = `${language}-`;
    return voiceTags.some((tag) => {
        const normalised = tag.toLowerCase().replace(/_/g, '-');
        return normalised === language || normalised.startsWith(prefix);
    });
}
/**
 * The language a BCP-47 tag names, when it is one this app can speak.
 *
 * Undefined rather than a default for anything else, and the difference
 * matters at the other end: dictation sends this to a transcription provider
 * as a hint, and a wrong hint is worse than none. Told "en", a recogniser
 * handed Arabic returns confident English nonsense; told nothing, it detects
 * for itself. So a French phone hints nothing and is transcribed correctly,
 * where defaulting it to English would not be.
 */
export function speechLanguageFromLocale(locale) {
    const tag = locale?.toLowerCase().replace(/_/g, '-') ?? '';
    if (tag === 'ar' || tag.startsWith('ar-'))
        return 'ar';
    if (tag === 'en' || tag.startsWith('en-'))
        return 'en';
    return undefined;
}
/**
 * How long a line an agent said stays over its head, in milliseconds.
 *
 * Read at roughly the pace a person reads a subtitle, with a floor so a
 * one-word answer is not gone before the eye reaches it and a ceiling so a
 * paragraph does not sit on the floor for half a minute. The full text is
 * always in the thread; the bubble is for the moment it was said.
 */
export function agentBubbleDuration(text) {
    return Math.min(14_000, Math.max(4_000, 2_500 + text.length * 45));
}
